import type { Locale } from 'shared';

/**
 * The one-liners that sit under the wordmark, one picked at random per visit.
 *
 * A bundled module rather than a fetched file, for the same reasons as the
 * news strip next door: it is a dozen short strings edited by hand, it must
 * render offline, and a request for it would be slower than the paint it is
 * part of. Edit the list, redeploy, done.
 *
 * The Spanish is a first pass and wants a native read before launch — these
 * are brand voice, not UI labels, and several of them turn on wordplay that
 * does not survive a literal translation.
 */
export type Tagline = Record<Locale, string>;

/** Typed non-empty so `randomTagline` always has something to fall back on. */
export const TAGLINES: [Tagline, ...Tagline[]] = [
  { en: 'See past the surface.', es: 'Mira más allá de la superficie.' },
  { en: 'Your inner vision, unlocked.', es: 'Tu visión interior, revelada.' },
  { en: 'Notice what’s unseen.', es: 'Nota lo que no se ve.' },
  { en: 'Wide awake from within.', es: 'Bien despierto desde dentro.' },
  { en: 'Truth in focus.', es: 'La verdad, enfocada.' },
  { en: 'Look closer with your eyes closed.', es: 'Mira más de cerca con los ojos cerrados.' },
  {
    en: 'Sight is a sense; insight is a superpower.',
    es: 'La vista es un sentido; la intuición es un superpoder.',
  },
  { en: 'Clear your mind to see the map.', es: 'Despeja la mente para ver el mapa.' },
  { en: 'The noise stops where the vision starts.', es: 'El ruido acaba donde empieza la visión.' },
  { en: 'Beyond the blink.', es: 'Más allá del parpadeo.' },
  { en: 'Inner lens.', es: 'Lente interior.' },
  { en: 'Rooted in intuition.', es: 'Con raíz en la intuición.' },
  { en: 'Quiet the noise, find the signal.', es: 'Calla el ruido, encuentra la señal.' },
];

/** One line, drawn uniformly from the list above. */
export const randomTagline = (): Tagline =>
  TAGLINES[Math.floor(Math.random() * TAGLINES.length)] ?? TAGLINES[0];
